/** Help article ids (TOC entries), longest first for subsection parent matching. */
const HELP_ARTICLE_IDS = [
  'guest-sedr-requirements',
  'guest-sedr-dashboard',
  'guest-settings',
  'guest-sedr-evidence',
  'sedr-scheduled-report',
  'guest-sedr-audit',
  'guest-information',
  'guest-quick-share',
  'getting-started',
  'sedr-requirements',
  'sedr-dashboard',
  'sedr-deletion',
  'sedr-evidence',
  'sedr-library',
  'guest-vault',
  'sedr-phases',
  'sedr-access',
  'sedr-settings',
  'sedr-setup',
  'sedr-audit',
  'guest-files',
  'guest-sedr',
  'information',
  'recovery',
  'settings',
  'sign-in',
  'admin',
  'files',
  'sedr',
  'sms',
  'vault',
] as const;

/** Legacy fragment invite (/s/…/g/…): includes “Opening an evidence link”. */
const GUEST_SEDR_LEGACY_ARTICLE_IDS = [
  'guest-sedr',
  'guest-sedr-dashboard',
  'guest-sedr-requirements',
  'guest-sedr-evidence',
  'guest-sedr-audit',
] as const;

/**
 * Logged-in account guest portal (/guest/…): full room help minus link-open instructions,
 * plus guest vault / settings / recovery.
 */
const GUEST_ACCOUNT_PORTAL_ARTICLE_IDS = [
  'guest-sedr-dashboard',
  'guest-sedr-requirements',
  'guest-sedr-evidence',
  'guest-sedr-audit',
  'guest-vault',
  'guest-settings',
  'recovery',
] as const;

const HELP_ALIASES: Record<string, string> = {
  passphrase: 'vault',
  'sedr-room': 'sedr',
};

/** Topics that require at least one of the listed app roles (same rules as nav). */
const TOPIC_REQUIRED_ROLES: Record<string, string[]> = {
  sms: ['SmsSender', 'Admin'],
  admin: ['Admin', 'Auditor'],
};

export type HelpAccessContext = {
  path: string;
  roles: string[];
};

function normalizePath(path: string): string {
  return path.replace(/\/+$/, '') || '/';
}

function hasAnyRole(roles: string[], required: string[]): boolean {
  return required.some((role) => roles.includes(role));
}

function isGuestAccountPortalPath(path: string): boolean {
  const normalized = normalizePath(path);
  return normalized === '/guest' || normalized.startsWith('/guest/');
}

/** True when the path is a guest information, folder, SEDR link, or account portal. */
export function isGuestHelpPath(path: string): boolean {
  return guestHelpTopicForPath(path) != null;
}

/** The default help topic for a guest path, or null if not a guest route. */
export function guestHelpTopicForPath(path: string): string | null {
  const normalized = normalizePath(path);
  if (/^\/i\/[^/]+$/.test(normalized)) return 'guest-information';
  if (/^\/f\/[^/]+\/g\/[^/]+$/.test(normalized)) return 'guest-files';
  if (normalized === '/share') return 'guest-quick-share';
  if (/^\/s\/[^/]+\/g\/[^/]+$/.test(normalized)) return 'guest-sedr';
  // Account guest portal (/guest/…)
  if (normalized === '/guest/settings' || normalized.startsWith('/guest/settings/')) {
    return 'guest-settings';
  }
  if (normalized === '/guest' || normalized.startsWith('/guest/')) {
    return 'guest-sedr-dashboard';
  }
  return null;
}

/** Article ids a guest path may open. */
export function guestAllowedArticleIds(path: string): readonly string[] | null {
  const defaultTopic = guestHelpTopicForPath(path);
  if (!defaultTopic) return null;
  if (isGuestAccountPortalPath(path)) return GUEST_ACCOUNT_PORTAL_ARTICLE_IDS;
  if (defaultTopic === 'guest-sedr') return GUEST_SEDR_LEGACY_ARTICLE_IDS;
  return [defaultTopic];
}

/** Map a topic id, alias, or in-article section id to its parent article id. */
export function parentHelpArticleId(topicId: string): string {
  const id = String(topicId || '').trim();
  if (!id) return id;
  const aliased = HELP_ALIASES[id];
  if (aliased) return aliased;
  if ((HELP_ARTICLE_IDS as readonly string[]).includes(id)) return id;
  for (const articleId of HELP_ARTICLE_IDS) {
    if (id.startsWith(`${articleId}-`)) return articleId;
  }
  return id;
}

/** Guest-only articles (shown on guest routes, hidden from main help). */
export function isGuestHelpArticle(articleId: string): boolean {
  return articleId.startsWith('guest-');
}

/** Whether the viewer may open this help topic (article, alias, or subsection). */
export function canAccessHelpTopic(topicId: string, ctx: HelpAccessContext): boolean {
  const articleId = parentHelpArticleId(topicId);
  const allowed = guestAllowedArticleIds(ctx.path);
  if (allowed) {
    // Account guests have no passkeys — block passkey subsections.
    if (topicId === 'settings-passkey' || topicId.endsWith('-passkey')) {
      return false;
    }
    return allowed.includes(articleId);
  }

  // Main / staff help: do not list or open guest manual pages.
  if (isGuestHelpArticle(articleId)) return false;

  const required = TOPIC_REQUIRED_ROLES[articleId];
  if (!required) return true;
  return hasAnyRole(ctx.roles, required);
}

export function filterHelpArticles<T extends { id: string }>(
  articles: T[],
  ctx: HelpAccessContext,
): T[] {
  return articles.filter((art) => canAccessHelpTopic(art.id, ctx));
}
